import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Plus, Trash2, ArrowRight, Download, Upload, Image as ImageIcon,
    FileText, TrendingUp, Calculator, Pencil, Layers, GitBranch, Box, Copy, Package, GitMerge,
} from "lucide-react";
import { toast } from "sonner";
import BulkUploadButton from "@/components/BulkUploadButton";
import CustomerCombobox from "@/components/CustomerCombobox";
import AssemblyManager from "@/components/AssemblyManager";
import PartEditDialog from "@/components/PartEditDialog";
import MasterCombobox from "@/components/MasterCombobox";

const STATUSES = ["Lead", "RFQ", "Feasibility", "Quotation Submitted", "Sample Submission", "Technical/Commercial Approval", "PO", "Loss/Regret"];
const SEGMENTS = ["Auto", "Non-Auto", "Domestic", "Export"];

function BomRoleIcon({ part }) {
    // 3 distinct icons + colors so the BOM hierarchy is visually obvious in the
    // parts list:
    //   L1 — pure assembly (Finished Good)            → indigo Layers
    //   L2 — sub-assembly (child AND assembly)        → amber GitMerge
    //   L3 — pure child (leaf)                        → zinc Package
    //   ——  standalone / legacy                       → zinc Box
    if (part.is_assembly && part.is_child) return <span title="Sub-assembly (Child + has its own children)" className="inline-flex items-center text-[#FFB703]" data-testid={`bom-icon-sub-${part.part_id}`}><GitMerge size={14} /></span>;
    if (part.is_assembly) return <span title="Assembly (Finished Good)" className="inline-flex items-center text-[#002FA7]" data-testid={`bom-icon-assembly-${part.part_id}`}><Layers size={14} /></span>;
    if (part.is_child) return <span title="Child Part (leaf)" className="inline-flex items-center text-zinc-500" data-testid={`bom-icon-child-${part.part_id}`}><Package size={14} /></span>;
    return <span title="Standalone Part" className="inline-flex items-center text-zinc-400" data-testid={`bom-icon-standalone-${part.part_id}`}><Box size={14} /></span>;
}

const STATUS_COLOR = {
    "Lead": "#52525B",
    "RFQ": "#002FA7",
    "Feasibility": "#5B6FE0",
    "Quotation Submitted": "#9C27B0",
    "Sample Submission": "#FFB703",
    "Technical/Commercial Approval": "#FF9800",
    "PO": "#2A9D8F",
    "Loss/Regret": "#E63946",
};

const emptyPart = {
    part_name: "", part_number: "", customer_name: "",
    annual_volume: 0, target_price: 0, sales_price: 0, po_volume: 0,
    end_application: "",
    segment: "Auto", status: "Lead",
    photo_path: "", project_years: 5, notes: "",
    bom_role: "standalone",
    child_rows: [],  // local-only — sent via separate POST/PUT on save
};

export default function Dashboard() {
    const [parts, setParts] = useState([]);
    const [stats, setStats] = useState({ counts: {}, total: 0 });
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editPart, setEditPart] = useState(null);   // when set → opens PartEditDialog instead of inline create dialog
    const [form, setForm] = useState(emptyPart);
    const [assemblyManagerFor, setAssemblyManagerFor] = useState(null);  // {part}
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterSegment, setFilterSegment] = useState("all");
    const [colFilter, setColFilter] = useState({
        part_name: "all",
        part_number: "all",
        customer_name: "all",
        segment: "all",
        status: "all",
        annual_min: "",
        annual_max: "",
        target_min: "",
        target_max: "",
    });
    const setCol = (k, v) => setColFilter((prev) => ({ ...prev, [k]: v }));
    const hasColFilter =
        colFilter.part_name !== "all" || colFilter.part_number !== "all" || colFilter.customer_name !== "all" ||
        colFilter.segment !== "all" || colFilter.status !== "all" ||
        colFilter.annual_min || colFilter.annual_max ||
        colFilter.target_min || colFilter.target_max;
    const clearColFilters = () => setColFilter({
        part_name: "all", part_number: "all", customer_name: "all", segment: "all", status: "all",
        annual_min: "", annual_max: "", target_min: "", target_max: "",
    });
    const [showChildren, setShowChildren] = useState(false);
    const [photoUploading, setPhotoUploading] = useState(false);
    const fileRef = useRef(null);
    const navigate = useNavigate();

    const load = async () => {
        const [p, s] = await Promise.all([api.get("/parts"), api.get("/parts/stats")]);
        setParts(p.data);
        setStats(s.data);
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const cf = colFilter;
        const numIn = (val, min, max) => {
            const v = Number(val) || 0;
            const lo = min === "" ? -Infinity : Number(min);
            const hi = max === "" ? Infinity : Number(max);
            return v >= lo && v <= hi;
        };
        const base = parts.filter((p) => {
            // Hide child parts from the CRM view (sales/proposal happens at FG level only)
            if (!showChildren && p.is_child) return false;
            if (filterStatus !== "all" && (p.status || "") !== filterStatus) return false;
            if (filterSegment !== "all" && (p.segment || "") !== filterSegment) return false;
            // Per-column filters (AND'd on top of global filters)
            if (cf.status !== "all" && (p.status || "") !== cf.status) return false;
            if (cf.segment !== "all" && (p.segment || "") !== cf.segment) return false;
            if (cf.part_name !== "all" && (p.part_name || "") !== cf.part_name) return false;
            if (cf.part_number !== "all" && (p.part_number || "") !== cf.part_number) return false;
            if (cf.customer_name !== "all" && (p.customer_name || "") !== cf.customer_name) return false;
            if (!numIn(p.annual_volume, cf.annual_min, cf.annual_max)) return false;
            const tgt = (p.status === "PO" && Number(p.sales_price) > 0) ? p.sales_price : p.target_price;
            if (!numIn(tgt, cf.target_min, cf.target_max)) return false;
            return true;
        });
        // When children are shown, slot every descendant recursively right under
        // their parent assembly so 1.1.1 (depth 2 child) appears under 1.1 which is
        // under 1. This is the same DFS the costing-sheet BOM tree uses.
        if (!showChildren) return base;
        const byId = new Map(parts.map((p) => [p.part_id, p]));
        const childrenByParent = new Map();
        const referencedAsChild = new Set();
        parts.forEach((p) => {
            if (!p.is_assembly || !p.child_parts) return;
            childrenByParent.set(p.part_id, p.child_parts.map((c) => byId.get(c.part_id)).filter(Boolean));
            (p.child_parts || []).forEach((c) => { if (c.part_id) referencedAsChild.add(c.part_id); });
        });
        const out = [];
        const pushed = new Set();
        const visit = (p, depth, outline) => {
            if (pushed.has(p.part_id)) return;
            out.push({ ...p, _indent: depth, _outline: outline });
            pushed.add(p.part_id);
            const kids = childrenByParent.get(p.part_id) || [];
            kids.forEach((k, i) => visit(k, depth + 1, outline ? `${outline}.${i + 1}` : `${i + 1}`));
        };
        let topIdx = 0;
        base.forEach((p) => {
            // Top-level = NOT referenced as a child by any other visible part.
            // Sub-assemblies (is_child + is_assembly) get nested under their parent.
            if (referencedAsChild.has(p.part_id)) return;
            topIdx += 1;
            visit(p, 0, String(topIdx));
        });
        // Append any orphan that didn't get visited (parent filtered out etc.)
        base.forEach((p) => { if (!pushed.has(p.part_id)) { out.push({ ...p, _indent: 0, _outline: "" }); pushed.add(p.part_id); } });
        return out;
    }, [parts, filterStatus, filterSegment, showChildren, colFilter]);
    const childCount = useMemo(() => parts.filter((p) => p.is_child).length, [parts]);
    // Unique values for the per-column dropdown filters (searchable comboboxes)
    const colOptions = useMemo(() => {
        const sortStr = (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" });
        const visibleParts = showChildren ? parts : parts.filter((p) => !p.is_child);
        const uniq = (key) => Array.from(new Set(visibleParts.map((p) => (p[key] || "").trim()).filter(Boolean))).sort(sortStr);
        const toItems = (arr) => [{ id: "all", label: "All" }, ...arr.map((s) => ({ id: s, label: s }))];
        return {
            part_names: toItems(uniq("part_name")),
            part_numbers: toItems(uniq("part_number")),
            customers: toItems(uniq("customer_name")),
        };
    }, [parts, showChildren]);

    const uploadPhoto = async (file) => {
        if (!file) return null;
        const fd = new FormData();
        fd.append("file", file);
        setPhotoUploading(true);
        try {
            const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            return res.data.path;
        } catch (e) {
            toast.error("Photo upload failed");
            return null;
        } finally {
            setPhotoUploading(false);
        }
    };

    const onPickPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const path = await uploadPhoto(file);
        if (path) setForm((f) => ({ ...f, photo_path: path }));
    };

    const save = async () => {
        if (!form.part_name.trim()) { toast.error("Part name required"); return; }
        const payload = {
            ...form,
            annual_volume: Number(form.annual_volume) || 0,
            target_price: Number(form.target_price) || 0,
            project_years: Number(form.project_years) || 5,
            sales_price: Number(form.sales_price) || 0,
            po_volume: Number(form.po_volume) || 0,
            end_application: form.end_application || "",
            is_assembly: form.bom_role === "assembly" || form.bom_role === "sub_assembly",
            is_child: form.bom_role === "child" || form.bom_role === "sub_assembly",
        };
        delete payload.bom_role;
        try {
            // Strip the local-only `child_rows` array before sending
            const { child_rows: inlineChildren, ...partPayload } = payload;
            let savedPart;
            if (editingId) {
                const r = await api.put(`/parts/${editingId}`, partPayload);
                savedPart = r.data;
                toast.success("Part updated");
            } else {
                const r = await api.post("/parts", partPayload);
                savedPart = r.data;
                toast.success("Part created");
            }

            // INLINE CHILD PARTS — only on create + when assembly + child_rows present.
            // Supports multi-level: each row can have `parent_idx` pointing to another
            // row's index. We walk top-down, create one Part per row, then update each
            // intermediate (parent_idx-having) row's parent part with the child_parts
            // reference. Top-level rows are linked to the just-created assembly.
            if (savedPart && savedPart.is_assembly && Array.isArray(inlineChildren) && inlineChildren.length > 0) {
                const partIdByIdx = {};
                const isSubAssemblyByIdx = {};
                // First pass: which rows are intermediates (have children)?
                inlineChildren.forEach((_, i) => { isSubAssemblyByIdx[i] = inlineChildren.some((r) => r.parent_idx === i); });
                // Create every row as a part (sub-assembly nodes are both child + assembly)
                for (let i = 0; i < inlineChildren.length; i++) {
                    const c = inlineChildren[i];
                    if (!c.part_name || !c.part_name.trim()) continue;
                    try {
                        const cr = await api.post("/parts", {
                            part_name: c.part_name.trim(),
                            part_number: c.part_number || "",
                            is_child: true,
                            is_assembly: !!isSubAssemblyByIdx[i],
                            project_years: 5,
                            status: "Lead",
                            segment: "Auto",
                            photo_path: c.photo_path || "",
                        });
                        partIdByIdx[i] = cr.data.part_id;
                    } catch (err) { toast.error(`Failed to create "${c.part_name}"`); }
                }
                // Second pass: link children to their parents (intermediates) and collect top-level
                const topLevel = [];
                const childrenByParent = {};
                inlineChildren.forEach((c, i) => {
                    if (!partIdByIdx[i]) return;
                    const ref = { part_id: partIdByIdx[i], quantity: Number(c.quantity) || 1 };
                    if (c.parent_idx == null) topLevel.push(ref);
                    else {
                        const ppid = partIdByIdx[c.parent_idx];
                        if (ppid) {
                            childrenByParent[ppid] = childrenByParent[ppid] || [];
                            childrenByParent[ppid].push(ref);
                        }
                    }
                });
                // Persist each intermediate's child_parts list
                for (const [ppid, refs] of Object.entries(childrenByParent)) {
                    try {
                        const parentPart = await api.get(`/parts/${ppid}`);
                        await api.put(`/parts/${ppid}`, { ...parentPart.data, child_parts: refs });
                    } catch (err) { /* ignore individual failures */ }
                }
                // Finally link top-level to the saved assembly
                if (topLevel.length > 0) {
                    await api.put(`/parts/${savedPart.part_id}`, { ...savedPart, child_parts: topLevel });
                    const totalCreated = Object.keys(partIdByIdx).length;
                    toast.success(`Linked ${topLevel.length} top-level child${topLevel.length > 1 ? "s" : ""} (${totalCreated} parts total across all levels)`);
                }
            }
            setOpen(false); setForm(emptyPart); setEditingId(null);
            await load();
        } catch { toast.error("Save failed"); }
    };

    const startEdit = (p) => {
        // Always use the unified PartEditDialog for editing existing parts
        // (gives full BOM tree drill-down + customer combobox + where-used banner).
        setEditPart(p);
    };

    const startCreate = () => {
        setEditingId(null);
        setForm(emptyPart);
        setOpen(true);
    };

    const onDialogChange = (v) => {
        setOpen(v);
        if (!v) { setEditingId(null); setForm(emptyPart); }
    };

    const updateField = async (part, field, value) => {
        const patch = { ...part, [field]: value };
        delete patch.part_id; delete patch.owner_id; delete patch.created_at; delete patch.updated_at;
        try {
            await api.put(`/parts/${part.part_id}`, patch);
            await load();
        } catch { toast.error("Update failed"); }
    };

    const [poDialogPart, setPoDialogPart] = useState(null);
    const [poVolume, setPoVolume] = useState(0);

    const handleStatusChange = async (part, newStatus) => {
        if (newStatus === "PO" && part.status !== "PO") {
            // Ask for PO volume
            setPoDialogPart(part);
            setPoVolume(part.po_volume || part.annual_volume || 0);
        } else {
            await updateField(part, "status", newStatus);
        }
    };

    const confirmPoVolume = async () => {
        if (!poDialogPart) return;
        const patch = { ...poDialogPart, status: "PO", po_volume: Number(poVolume) || 0 };
        delete patch.part_id; delete patch.owner_id; delete patch.created_at; delete patch.updated_at;
        try {
            await api.put(`/parts/${poDialogPart.part_id}`, patch);
            toast.success(`PO confirmed at ${Number(poVolume).toLocaleString()} units`);
            setPoDialogPart(null);
            await load();
        } catch { toast.error("Update failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this part and all its drafts?")) return;
        await api.delete(`/parts/${id}`); await load();
        toast.success("Deleted");
    };

    const duplicatePart = async (p) => {
        try {
            const r = await api.post(`/parts/${p.part_id}/duplicate`);
            toast.success(`Duplicated: ${r.data.part_name}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Duplicate failed");
        }
    };

    const onUploadPartPhoto = async (part, file) => {
        const path = await uploadPhoto(file);
        if (path) await updateField(part, "photo_path", path);
    };

    return (
        <div>
            <PageHeader
                eyebrow="Parts CRM"
                title="Pipeline"
                subtitle="Track every part from lead to PO. Each part has its own costing, business proposal and ROI."
                actions={
                    <div className="flex gap-2">
                        <BulkUploadButton entity="parts" onComplete={load} />
                        <Button variant="outline" className="rounded-none" onClick={() => window.open(`${API}/parts/export`, "_blank")} data-testid="export-parts-button">
                            <Download size={14} className="mr-2" /> Export Excel
                        </Button>
                        <Dialog open={open} onOpenChange={onDialogChange}>
                            <DialogTrigger asChild>
                                <Button className="rounded-none bg-[#E63946] hover:bg-[#C92836]" onClick={startCreate} data-testid="new-part-button">
                                    <Plus size={16} className="mr-2" /> New Part
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-none max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                                <DialogHeader><DialogTitle>{editingId ? "Edit Part" : "New Part"}</DialogTitle></DialogHeader>
                                <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1 pr-2 -mr-2" data-testid="part-dialog-body">
                                    <Field label="Part Name *">
                                        <Input data-testid="part-name-input" value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} />
                                    </Field>
                                    <Field label="Part Number">
                                        <Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} />
                                    </Field>
                                    <Field label="BOM Role *">
                                        <Select value={form.bom_role || "standalone"} onValueChange={(v) => setForm({ ...form, bom_role: v })}>
                                            <SelectTrigger data-testid="part-bom-role" className="rounded-none"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="assembly">Finished Good (Assembly)</SelectItem>
                                                <SelectItem value="child">Child Part (Sub-component)</SelectItem>
                                                <SelectItem value="sub_assembly">Sub-assembly (Child + has its own children)</SelectItem>
                                                <SelectItem value="standalone">Standalone / Legacy</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    {form.bom_role !== "child" && (<>
                                    <Field label="Customer Name">
                                        <CustomerCombobox value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="new-customer" />
                                    </Field>
                                    <Field label="Segment">
                                        <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                                            <SelectTrigger data-testid="part-segment-select" className="rounded-none"><SelectValue /></SelectTrigger>
                                            <SelectContent>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Status">
                                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                            <SelectTrigger data-testid="part-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Project Years (for ROI)">
                                        <Input type="number" min="1" max="20" value={form.project_years} onChange={(e) => setForm({ ...form, project_years: e.target.value })} />
                                    </Field>
                                    <Field label="Annual Volume (Projected)">
                                        <Input type="number" value={form.annual_volume} onChange={(e) => setForm({ ...form, annual_volume: e.target.value })} />
                                    </Field>
                                    <Field label="Target Price (Rs/piece)">
                                        <Input type="number" value={form.target_price} onChange={(e) => setForm({ ...form, target_price: e.target.value })} />
                                    </Field>
                                    {form.status === "PO" && (<>
                                        <Field label="PO Volume (Confirmed)">
                                            <Input type="number" value={form.po_volume ?? 0} onChange={(e) => setForm({ ...form, po_volume: e.target.value })} data-testid="part-po-volume-input" />
                                        </Field>
                                        <Field label="Sales Price (Rs/piece — confirmed PO)">
                                            <Input type="number" value={form.sales_price ?? 0} onChange={(e) => setForm({ ...form, sales_price: e.target.value })} data-testid="part-sales-price-input" />
                                        </Field>
                                    </>)}
                                    <Field label="End Application">
                                        <Input value={form.end_application || ""} placeholder="e.g. EV battery housing"
                                            onChange={(e) => setForm({ ...form, end_application: e.target.value })}
                                            data-testid="part-end-app-input" />
                                    </Field>
                                    </>)}
                                    <div className="col-span-2">
                                        <Field label="Photo">
                                            <div className="flex items-center gap-3">
                                                {form.photo_path ? (
                                                    <img src={`${API}/files/${form.photo_path}`} alt="part" className="w-16 h-16 object-cover border border-zinc-200" />
                                                ) : (
                                                    <div className="w-16 h-16 border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400">
                                                        <ImageIcon size={20} />
                                                    </div>
                                                )}
                                                <input ref={fileRef} type="file" accept="image/*" onChange={onPickPhoto} className="hidden" data-testid="part-photo-input" />
                                                <Button type="button" variant="outline" className="rounded-none" onClick={() => fileRef.current?.click()} disabled={photoUploading}>
                                                    <Upload size={14} className="mr-2" /> {photoUploading ? "Uploading…" : "Upload Photo"}
                                                </Button>
                                            </div>
                                        </Field>
                                    </div>
                                    <div className="col-span-2">
                                        <Field label="Notes">
                                            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                                        </Field>
                                    </div>
                                    {(form.bom_role === "assembly" || form.bom_role === "sub_assembly") && !editingId && (
                                        <div className="col-span-2 border-t border-zinc-200 pt-3 mt-2" data-testid="inline-child-parts-section">
                                            <div className="flex items-center justify-between mb-2">
                                                <Label className="text-[11px] uppercase tracking-wider text-zinc-500 font-mono">BOM tree (1, 1.1, 1.1.1 …)</Label>
                                                <button
                                                    type="button"
                                                    onClick={() => setForm({ ...form, child_rows: [...(form.child_rows || []), { part_name: "", part_number: "", quantity: 1, photo_path: "", parent_idx: null }] })}
                                                    className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline font-mono"
                                                    data-testid="add-inline-child-btn"
                                                >+ Add level-1 child</button>
                                            </div>
                                            {(form.child_rows || []).length === 0 ? (
                                                <div className="text-xs text-zinc-400 italic border border-dashed border-zinc-200 py-3 text-center">No child parts yet. Click &ldquo;+ Add level-1 child&rdquo; to attach.</div>
                                            ) : (() => {
                                                // Compute outline numbers using parent_idx graph
                                                const rows = form.child_rows;
                                                const childrenOf = (parentIdx) => rows.map((r, i) => ({ r, i })).filter((x) => (x.r.parent_idx ?? null) === parentIdx);
                                                const numberMap = {};
                                                const walk = (parentIdx, prefix) => {
                                                    childrenOf(parentIdx).forEach((node, ord) => {
                                                        const num = prefix ? `${prefix}.${ord + 1}` : `${ord + 1}`;
                                                        numberMap[node.i] = num;
                                                        walk(node.i, num);
                                                    });
                                                };
                                                walk(null, "");
                                                // Render in DFS order using the same walk
                                                const out = [];
                                                const render = (parentIdx, depth) => {
                                                    childrenOf(parentIdx).forEach((node) => {
                                                        const i = node.i;
                                                        const c = node.r;
                                                        const number = numberMap[i] || "?";
                                                        out.push(
                                                            <div key={i} className="grid grid-cols-[40px_60px_2fr_1fr_70px_60px] gap-2 items-center mb-2" data-testid={`inline-child-row-${i}`} style={{ paddingLeft: depth * 20 }}>
                                                                <span className="text-[10px] font-mono font-semibold text-[#002FA7]" data-testid={`inline-child-number-${i}`}>{number}</span>
                                                                <ChildPhotoUploader
                                                                    path={c.photo_path}
                                                                    onUploaded={async (file) => {
                                                                        const p = await uploadPhoto(file);
                                                                        if (p) {
                                                                            const next = [...form.child_rows]; next[i] = { ...next[i], photo_path: p };
                                                                            setForm({ ...form, child_rows: next });
                                                                        }
                                                                    }}
                                                                    index={i}
                                                                />
                                                                <Input placeholder="Child name" value={c.part_name} onChange={(e) => { const next = [...form.child_rows]; next[i] = { ...next[i], part_name: e.target.value }; setForm({ ...form, child_rows: next }); }} className="rounded-none h-8" data-testid={`inline-child-name-${i}`} />
                                                                <Input placeholder="Part #" value={c.part_number} onChange={(e) => { const next = [...form.child_rows]; next[i] = { ...next[i], part_number: e.target.value }; setForm({ ...form, child_rows: next }); }} className="rounded-none h-8" />
                                                                <Input type="number" min="0.01" step="1" placeholder="Qty" value={c.quantity} onChange={(e) => { const next = [...form.child_rows]; next[i] = { ...next[i], quantity: e.target.value }; setForm({ ...form, child_rows: next }); }} className="rounded-none h-8 font-mono" data-testid={`inline-child-qty-${i}`} />
                                                                <div className="flex items-center gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setForm({ ...form, child_rows: [...form.child_rows, { part_name: "", part_number: "", quantity: 1, photo_path: "", parent_idx: i }] })}
                                                                        className="text-[#002FA7] hover:underline text-[10px] uppercase tracking-wider"
                                                                        title="Add sub-child under this row"
                                                                        data-testid={`inline-child-add-sub-${i}`}
                                                                    >+sub</button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            // Remove this row + any descendants
                                                                            const drop = new Set([i]);
                                                                            let changed = true;
                                                                            while (changed) {
                                                                                changed = false;
                                                                                form.child_rows.forEach((r, idx) => {
                                                                                    if (r.parent_idx != null && drop.has(r.parent_idx) && !drop.has(idx)) { drop.add(idx); changed = true; }
                                                                                });
                                                                            }
                                                                            // Rebuild with index remap
                                                                            const remap = {};
                                                                            const next = [];
                                                                            form.child_rows.forEach((r, idx) => { if (!drop.has(idx)) { remap[idx] = next.length; next.push(r); } });
                                                                            const fixed = next.map((r) => ({ ...r, parent_idx: r.parent_idx == null ? null : (remap[r.parent_idx] ?? null) }));
                                                                            setForm({ ...form, child_rows: fixed });
                                                                        }}
                                                                        className="text-zinc-400 hover:text-[#E63946]"
                                                                        data-testid={`inline-child-remove-${i}`}
                                                                    ><Trash2 size={14} /></button>
                                                                </div>
                                                            </div>
                                                        );
                                                        render(i, depth + 1);
                                                    });
                                                };
                                                render(null, 0);
                                                return out;
                                            })()}
                                        </div>
                                    )}
                                </div>
                                <DialogFooter>
                                    <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                                    <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={save} data-testid="save-part-button">Create</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                }
            />

            <div className="px-8 py-6 space-y-6">
                {/* Hit Rate KPI Strip */}
                <HitRateStrip />

                {/* Pipeline Stage Cards */}
                <div className="grid grid-cols-5 gap-3" data-testid="pipeline-stage-cards">
                    {STATUSES.map((s) => {
                        const n = stats.counts?.[s] || 0;
                        return (
                            <button
                                key={s}
                                onClick={() => setFilterStatus(filterStatus === s ? "all" : s)}
                                data-testid={`stage-card-${s.replace(/\s|\//g,"-")}`}
                                className={`text-left border p-4 transition-colors ${filterStatus === s ? "border-[#09090b]" : "border-zinc-200 hover:border-zinc-400"}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{s}</div>
                                    <div className="w-2 h-2" style={{ background: STATUS_COLOR[s] }} />
                                </div>
                                <div className="text-3xl font-bold font-mono mt-2">{n}</div>
                            </button>
                        );
                    })}
                </div>

                {/* Filters / search */}
                <div className="flex items-center gap-3 flex-wrap border-y border-zinc-200 py-3">
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger data-testid="filter-status" className="rounded-none w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filterSegment} onValueChange={setFilterSegment}>
                        <SelectTrigger data-testid="filter-segment" className="rounded-none w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Segments</SelectItem>
                            {SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <div className="text-xs text-zinc-500 font-mono">
                        Showing <span className="font-semibold">{filtered.length}</span> of {parts.length}
                    </div>
                    {childCount > 0 && (
                        <button
                            onClick={() => setShowChildren((v) => !v)}
                            className="text-xs uppercase tracking-wider text-[#002FA7] hover:underline ml-2"
                            data-testid="toggle-show-children"
                        >
                            {showChildren ? `Hide ${childCount} child part(s)` : `Show ${childCount} child part(s)`}
                        </button>
                    )}
                    {hasColFilter && (
                        <button
                            onClick={clearColFilters}
                            className="text-xs uppercase tracking-wider text-[#E63946] hover:underline ml-2"
                            data-testid="clear-col-filters"
                        >
                            Clear column filters
                        </button>
                    )}
                </div>

                {/* Table */}
                {filtered.length === 0 ? (
                    <EmptyState onCreate={() => setOpen(true)} hasFilters={filterStatus !== "all" || filterSegment !== "all" || hasColFilter} />
                ) : (
                    <table className="w-full sheet-table" data-testid="parts-table">
                        <thead>
                            <tr>
                                <th style={{ width: 60 }}>Photo</th>
                                <th>Part</th>
                                <th>Part No.</th>
                                <th>Customer</th>
                                <th style={{ width: 130 }}>Segment</th>
                                <th style={{ width: 180 }}>Status</th>
                                <th>Annual Vol.</th>
                                <th>Target / Sales ₹</th>
                                <th style={{ width: 90 }}>Costing</th>
                                <th style={{ width: 150 }}>Business Proposal</th>
                                <th></th>
                            </tr>
                            <tr className="bg-zinc-50" data-testid="col-filter-row">
                                <th></th>
                                <th>
                                    <MasterCombobox
                                        items={colOptions.part_names}
                                        value={colFilter.part_name}
                                        onChange={(id) => setCol("part_name", id)}
                                        placeholder="All"
                                        testIdPrefix="col-filter-part-name"
                                    />
                                </th>
                                <th>
                                    <MasterCombobox
                                        items={colOptions.part_numbers}
                                        value={colFilter.part_number}
                                        onChange={(id) => setCol("part_number", id)}
                                        placeholder="All"
                                        testIdPrefix="col-filter-part-number"
                                    />
                                </th>
                                <th>
                                    <MasterCombobox
                                        items={colOptions.customers}
                                        value={colFilter.customer_name}
                                        onChange={(id) => setCol("customer_name", id)}
                                        placeholder="All"
                                        testIdPrefix="col-filter-customer"
                                    />
                                </th>
                                <th>
                                    <Select value={colFilter.segment} onValueChange={(v) => setCol("segment", v)}>
                                        <SelectTrigger className="h-7 rounded-none text-xs px-2" data-testid="col-filter-segment"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All</SelectItem>
                                            {SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </th>
                                <th>
                                    <Select value={colFilter.status} onValueChange={(v) => setCol("status", v)}>
                                        <SelectTrigger className="h-7 rounded-none text-xs px-2" data-testid="col-filter-status"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All</SelectItem>
                                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </th>
                                <th>
                                    <div className="flex items-center gap-1">
                                        <Input
                                            type="number"
                                            value={colFilter.annual_min}
                                            onChange={(e) => setCol("annual_min", e.target.value)}
                                            placeholder="Min"
                                            className="h-7 rounded-none text-xs px-2 font-mono"
                                            data-testid="col-filter-annual-min"
                                        />
                                        <Input
                                            type="number"
                                            value={colFilter.annual_max}
                                            onChange={(e) => setCol("annual_max", e.target.value)}
                                            placeholder="Max"
                                            className="h-7 rounded-none text-xs px-2 font-mono"
                                            data-testid="col-filter-annual-max"
                                        />
                                    </div>
                                </th>
                                <th>
                                    <div className="flex items-center gap-1">
                                        <Input
                                            type="number"
                                            value={colFilter.target_min}
                                            onChange={(e) => setCol("target_min", e.target.value)}
                                            placeholder="Min"
                                            className="h-7 rounded-none text-xs px-2 font-mono"
                                            data-testid="col-filter-target-min"
                                        />
                                        <Input
                                            type="number"
                                            value={colFilter.target_max}
                                            onChange={(e) => setCol("target_max", e.target.value)}
                                            placeholder="Max"
                                            className="h-7 rounded-none text-xs px-2 font-mono"
                                            data-testid="col-filter-target-max"
                                        />
                                    </div>
                                </th>
                                <th></th>
                                <th></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p) => (
                                <PartRow
                                    key={p.part_id}
                                    p={p}
                                    onUpdate={(field, value) => updateField(p, field, value)}
                                    onStatusChange={(v) => handleStatusChange(p, v)}
                                    onUploadPhoto={(file) => onUploadPartPhoto(p, file)}
                                    onDelete={() => remove(p.part_id)}
                                    onDuplicate={() => duplicatePart(p)}
                                    onEdit={() => startEdit(p)}
                                    onOpenCosting={() => navigate(`/parts/${p.part_id}/costing`)}
                                    onOpenProposal={() => navigate(`/parts/${p.part_id}/proposal`)}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <PartEditDialog
                open={!!editPart}
                onOpenChange={(v) => { if (!v) setEditPart(null); }}
                part={editPart}
                onSaved={() => { setEditPart(null); load(); }}
            />

            <Dialog open={!!poDialogPart} onOpenChange={(v) => { if (!v) setPoDialogPart(null); }}>
                <DialogContent className="rounded-none max-w-sm" data-testid="po-volume-dialog">
                    <DialogHeader><DialogTitle>Confirm PO Volume</DialogTitle></DialogHeader>
                    <p className="text-sm text-zinc-500">
                        Marking <strong>{poDialogPart?.part_name}</strong> as PO. Enter the confirmed PO volume — this is what counts as <em>confirmed sales</em> in the Sales Dashboard.
                    </p>
                    <div className="space-y-2 mt-2">
                        <Label className="text-[11px] uppercase tracking-wider text-zinc-500">PO Volume</Label>
                        <Input
                            type="number" min="0"
                            value={poVolume}
                            onChange={(e) => setPoVolume(e.target.value)}
                            className="font-mono rounded-none"
                            autoFocus
                            data-testid="po-volume-input"
                        />
                        <div className="text-[11px] text-zinc-400 font-mono">Annual planning volume is {poDialogPart?.annual_volume?.toLocaleString() || 0}. Adjust for the actual PO if different.</div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setPoDialogPart(null)}>Cancel</Button>
                        <Button className="rounded-none bg-[#2A9D8F] hover:bg-[#218377]" onClick={confirmPoVolume} data-testid="confirm-po-volume">Confirm PO</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AssemblyManager
                open={!!assemblyManagerFor}
                onOpenChange={(v) => { if (!v) setAssemblyManagerFor(null); }}
                part={assemblyManagerFor || {}}
                onSaved={async () => { await load(); setAssemblyManagerFor(null); }}
            />
        </div>
    );
}

function PartRow({ p, onUpdate, onStatusChange, onUploadPhoto, onDelete, onDuplicate, onEdit, onOpenCosting, onOpenProposal }) {
    const fileRef = useRef(null);
    return (
        <tr data-testid={`part-row-${p.part_id}`}>
            <td>
                <input ref={fileRef} type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && onUploadPhoto(e.target.files[0])} className="hidden" />
                {p.photo_path ? (
                    <img
                        src={`${API}/files/${p.photo_path}`}
                        alt={p.part_name}
                        className="w-12 h-12 object-cover border border-zinc-200 cursor-pointer"
                        onClick={() => fileRef.current?.click()}
                        title="Click to change"
                    />
                ) : (
                    <button onClick={() => fileRef.current?.click()} className="w-12 h-12 border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400 hover:border-zinc-500" data-testid={`upload-photo-${p.part_id}`}>
                        <ImageIcon size={16} />
                    </button>
                )}
            </td>
            <td className="font-medium"><span className="inline-flex items-center gap-2" style={{ paddingLeft: (p._indent || 0) * 20 }}>{p._indent ? <span className="text-zinc-300">↳</span> : null}{p._outline && <span className="text-[9px] font-mono text-[#002FA7] bg-zinc-100 px-1 py-0.5" data-testid={`bom-outline-${p.part_id}`}>{p._outline}</span>}{<BomRoleIcon part={p} />} {p.part_name}</span></td>
            <td className="font-mono text-xs">{p.part_number || "-"}</td>
            <td>{p.customer_name || "-"}</td>
            <td>
                <Select value={p.segment || ""} onValueChange={(v) => onUpdate("segment", v)}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`segment-select-${p.part_id}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
            </td>
            <td>
                <Select value={p.status || ""} onValueChange={onStatusChange}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`status-select-${p.part_id}`}>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2" style={{ background: STATUS_COLOR[p.status] || "#a1a1aa" }} />
                            <SelectValue placeholder="—" />
                        </div>
                    </SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
            </td>
            <td className="font-mono">{p.annual_volume?.toLocaleString()}</td>
            <td className="font-mono">
                {p.status === "PO" ? (
                    <span data-testid={`sales-price-${p.part_id}`}>
                        {(p.sales_price ?? p.target_price)?.toLocaleString()}
                        <span className="ml-1 text-[9px] uppercase tracking-wider text-[#2A9D8F]">sales</span>
                    </span>
                ) : (
                    <span data-testid={`target-price-${p.part_id}`}>{p.target_price?.toLocaleString()}</span>
                )}
            </td>
            <td>
                <button onClick={onOpenCosting} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline text-xs uppercase tracking-wider font-mono" data-testid={`open-costing-${p.part_id}`}>
                    <Calculator size={12} /> Open
                </button>
            </td>
            <td>
                <button onClick={onOpenProposal} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline text-xs uppercase tracking-wider font-mono" data-testid={`open-proposal-${p.part_id}`}>
                    <FileText size={12} /> Proposal
                </button>
                <button
                    onClick={() => window.location.assign(`/tasks?part_id=${p.part_id}`)}
                    className="ml-2 inline-flex items-center gap-1 text-[#9C27B0] hover:underline text-xs uppercase tracking-wider font-mono"
                    data-testid={`open-tasks-${p.part_id}`}
                    title="Open tasks for this part"
                >
                    Tasks
                </button>
            </td>
            <td className="text-right w-16">
                <div className="inline-flex items-center gap-1">
                    <button onClick={onEdit} title="Edit part" className="text-zinc-400 hover:text-[#002FA7]" data-testid={`edit-part-${p.part_id}`}>
                        <Pencil size={14} />
                    </button>
                    <button onClick={onDuplicate} title="Duplicate part" className="text-zinc-400 hover:text-[#002FA7]" data-testid={`duplicate-${p.part_id}`}>
                        <Copy size={14} />
                    </button>
                    <button onClick={onDelete} title="Delete" className="text-zinc-400 hover:text-[#E63946]">
                        <Trash2 size={14} />
                    </button>
                </div>
            </td>
        </tr>
    );
}

function ChildPhotoUploader({ path, onUploaded, index }) {
    const ref = React.useRef(null);
    return (
        <div className="relative">
            <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploaded(f); e.target.value = ""; }} />
            <button
                type="button"
                onClick={() => ref.current?.click()}
                className="w-14 h-14 border border-zinc-200 hover:border-[#002FA7] flex items-center justify-center overflow-hidden"
                data-testid={`inline-child-photo-${index}`}
            >
                {path ? (
                    <img src={`${API}/files/${path}`} alt="child" className="w-full h-full object-cover" />
                ) : (
                    <ImageIcon size={16} className="text-zinc-400" />
                )}
            </button>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
            {children}
        </div>
    );
}

function EmptyState({ onCreate, hasFilters }) {
    return (
        <div className="border-2 border-dashed border-zinc-300 p-16 text-center">
            <div className="tag-pill mb-4 inline-flex">{hasFilters ? "No match" : "Empty"}</div>
            <h2 className="text-2xl font-bold tracking-tight">{hasFilters ? "No parts match your filters" : "No parts yet"}</h2>
            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
                {hasFilters ? "Adjust the search/filter, or create a new part." : "Start by adding a part with its customer, segment and target price."}
            </p>
            <Button onClick={onCreate} className="mt-6 rounded-none bg-[#002FA7] hover:bg-[#00227B]" data-testid="empty-create-part">
                <Plus size={16} className="mr-2" /> Create part
            </Button>
        </div>
    );
}

function HitRateStrip() {
    const [data, setData] = useState(null);
    const [overdue, setOverdue] = useState(0);
    useEffect(() => {
        api.get("/hit-rate").then((r) => setData(r.data)).catch(() => {});
        api.get("/tasks/overdue/summary").then((r) => setOverdue(r.data.count || 0)).catch(() => {});
    }, []);
    if (!data) return null;
    const segments = Object.entries(data.by_segment || {});
    return (
        <div className="grid grid-cols-12 gap-3" data-testid="hit-rate-strip">
            <div className="col-span-3 border-2 border-[#2A9D8F] p-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Hit Rate · PO / Total parts</div>
                <div className="text-4xl font-bold font-mono text-[#2A9D8F] mt-1" data-testid="hit-rate-value">{data.hit_rate_pct}%</div>
                <div className="text-xs text-zinc-500 font-mono mt-1">{data.po} PO of {data.total_parts} total parts</div>
            </div>
            <div className="col-span-6 border border-zinc-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">By Segment</div>
                <div className="flex flex-wrap gap-3">
                    {segments.length === 0 && <span className="text-xs text-zinc-400 italic">No segment data yet</span>}
                    {segments.map(([seg, v]) => {
                        const rate = v.rfq_or_later > 0 ? Math.round((v.po / v.rfq_or_later) * 100) : 0;
                        return (
                            <div key={seg} className="text-xs flex flex-col">
                                <span className="text-zinc-500 font-mono uppercase tracking-wider text-[10px]">{seg}</span>
                                <span className="font-mono font-semibold">{rate}% <span className="text-zinc-400">({v.po}/{v.rfq_or_later})</span></span>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="col-span-3 border border-zinc-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Overdue Tasks</div>
                <div className={`text-4xl font-bold font-mono mt-1 ${overdue > 0 ? "text-[#E63946]" : "text-zinc-400"}`} data-testid="overdue-count">{overdue}</div>
                <div className="text-xs text-zinc-500 font-mono mt-1">
                    <NavLink to="/tasks" className="text-[#002FA7] hover:underline">Open tasks →</NavLink>
                </div>
            </div>
        </div>
    );
}



// keep ArrowRight import unused-friendly
export const _ArrowRight = ArrowRight;
